import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Storage key for invitation token
const INVITATION_TOKEN_KEY = 'invitation_token';

interface InvitationAcceptStore {
  // State
  invitationToken: string | null;
  email: string | null;
  organizationName: string | null;
  role: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setInvitationToken: (token: string | null) => void;
  setInvitationData: (data: {
    email?: string;
    organizationName?: string;
    role?: string;
  }) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearInvitation: () => void;
}

const initialState = {
  invitationToken: null,
  email: null,
  organizationName: null,
  role: null,
  isLoading: false,
  error: null,
};

export const useInvitationAcceptStore = create<InvitationAcceptStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Initialize from sessionStorage if available
      invitationToken: (() => {
        if (typeof window === 'undefined') return null;
        try {
          return sessionStorage.getItem(INVITATION_TOKEN_KEY);
        } catch {
          return null;
        }
      })(),

      setInvitationToken: (token: string | null) => {
        if (typeof window === 'undefined') return;

        if (token) {
          try {
            sessionStorage.setItem(INVITATION_TOKEN_KEY, token);
            set({ invitationToken: token });
          } catch (error) {
            console.error('Failed to save invitation token:', error);
          }
        } else {
          try {
            sessionStorage.removeItem(INVITATION_TOKEN_KEY);
            set({ invitationToken: null });
          } catch (error) {
            console.error('Failed to remove invitation token:', error);
          }
        }
      },

      setInvitationData: (data) =>
        set((state) => ({
          email: data.email ?? state.email,
          organizationName: data.organizationName ?? state.organizationName,
          role: data.role ?? state.role,
        })),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => set({ error }),

      clearInvitation: () => {
        if (typeof window === 'undefined') return;

        try {
          sessionStorage.removeItem(INVITATION_TOKEN_KEY);
        } catch (error) {
          console.error('Failed to clear invitation token:', error);
        }

        set({
          invitationToken: null,
          email: null,
          organizationName: null,
          role: null,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'invitation-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        invitationToken: state.invitationToken,
        organizationName: state.organizationName,
        email: state.email,
      }),
    }
  )
);
